'use client';

import { useMemo } from 'react';
import dynamic from 'next/dynamic';
import type { ApexOptions } from 'apexcharts';
import { useThemeMode } from '@terra/ui-design-system';
import { getTerraGraphCategoryColors, token } from '@terra/ui-styled-system';
import {
  type LineTooltipParams,
  formatAxisLabel,
  formatTooltipValue,
} from './format';

const ReactApexChart = dynamic(() => import('react-apexcharts'), {
  ssr: false,
});

export interface BarChartSeries {
  name: string;
  data: (number | null)[];
  stdDev?: number[];
}

export interface BarChartProps {
  series: BarChartSeries[];
  categories: (string | string[])[];
  title?: string;
  height?: number | string;
  width?: number | string;
  colors?: string[];
  stacked?: boolean;
  horizontal?: boolean;
  distributed?: boolean;
  columnWidth?: string;
  borderRadius?: number;
  xaxisTitle?: string;
  yaxisTitle?: string;
  showDataLabels?: boolean;
  showStdDevLabels?: boolean;
  dataLabelsFormatter?: (
    value: number,
    context: { dataPointIndex: number; seriesIndex: number },
  ) => string;
  tooltipCustom?: (options: LineTooltipParams) => string;
  tooltipFormatter?: (value: number) => string;
  onBarDataPointClick?: ({
    seriesIndex,
    dataPointIndex,
  }: {
    seriesIndex: number;
    dataPointIndex: number;
  }) => void;
  showLegend?: boolean;
  legendPosition?: 'top' | 'right' | 'bottom' | 'left';
  showTotalDataLabels?: boolean;
  showAllCategoryLabels?: boolean;
  xAxisConfig?: ApexOptions['xaxis'];
  yAxisConfig?: ApexOptions['yaxis'];
  gridConfig?: ApexOptions['grid'];
  legendConfig?: ApexOptions['legend'];
  fillConfig?: ApexOptions['fill'];
  axisTitleStyle?: {
    color?: string;
    fontSize?: string;
    fontFamily?: string;
    fontWeight?: string;
    cssClass?: string;
  };
  legendOffsetY?: number;
  yaxisMin?: number;
  yaxisMax?: number;
  yaxisLabelFormatter?: (value: number) => string;
}

export function BarChart({
  series,
  categories,
  title = '',
  height = 350,
  width = '100%',
  colors = getTerraGraphCategoryColors(),
  stacked = false,
  horizontal = false,
  distributed = false,
  columnWidth = '55%',
  borderRadius = 4,
  xaxisTitle = '',
  yaxisTitle = '',
  showDataLabels = false,
  showStdDevLabels = false,
  dataLabelsFormatter,
  tooltipCustom,
  tooltipFormatter,
  onBarDataPointClick,
  showLegend = true,
  legendPosition = 'bottom',
  showTotalDataLabels = false,
  showAllCategoryLabels = false,
  xAxisConfig,
  yAxisConfig,
  gridConfig,
  legendConfig,
  fillConfig,
  axisTitleStyle,
  legendOffsetY,
  yaxisMin,
  yaxisMax,
  yaxisLabelFormatter,
}: BarChartProps) {
  const { mode: themeMode } = useThemeMode();
  const isDark = themeMode === 'dark';

  const labelColor = isDark
    ? token('colors.neutral.dark.12')
    : token('colors.neutral.light.12');
  const mutedLabelColor = isDark
    ? token('colors.neutral.dark.11')
    : token('colors.neutral.light.11');
  const chartBackground = isDark
    ? token('colors.neutral.dark.2')
    : token('colors.neutral.light.1');
  const defaultGridColor = isDark
    ? token('colors.neutral.dark.6')
    : token('colors.neutral.light.6');

  const resolvedShowDataLabels = showDataLabels || showStdDevLabels;

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: 'bar',
        toolbar: {
          show: false,
        },
        stacked: stacked,
        background: chartBackground,
        foreColor: labelColor,
        fontFamily: 'inherit',
        ...(onBarDataPointClick
          ? {
              events: {
                dataPointSelection: (
                  _event: unknown,
                  _chartContext: unknown,
                  opts: { seriesIndex: number; dataPointIndex: number },
                ) => {
                  const { dataPointIndex, seriesIndex } = opts;

                  if (seriesIndex < 0 || dataPointIndex < 0) {
                    return;
                  }

                  onBarDataPointClick({ seriesIndex, dataPointIndex });
                },
              },
            }
          : {}),
      },
      theme: {
        mode: isDark ? 'dark' : 'light',
      },
      plotOptions: {
        bar: {
          horizontal: horizontal,
          distributed: distributed,
          columnWidth: columnWidth,
          borderRadius: borderRadius,
          dataLabels: {
            position: horizontal ? 'center' : 'top',
            total: {
              enabled: showTotalDataLabels,
              offsetX: horizontal ? 4 : 0,
              offsetY: 0,
              style: {
                fontSize: '12px',
                fontWeight: 'normal',
                color: mutedLabelColor,
              },
            },
          },
        },
      },
      colors: colors,
      dataLabels: {
        enabled: resolvedShowDataLabels,
        offsetY: horizontal ? 0 : -16,
        style: {
          fontSize: '10px',
          colors: [mutedLabelColor],
          fontFamily: 'inherit',
        },
        formatter: (
          value: number,
          opts: { seriesIndex: number; dataPointIndex: number },
        ) => {
          if (dataLabelsFormatter) {
            return dataLabelsFormatter(value, {
              dataPointIndex: opts.dataPointIndex,
              seriesIndex: opts.seriesIndex,
            });
          }

          if (showStdDevLabels) {
            const std =
              series[opts.seriesIndex]?.stdDev?.[opts.dataPointIndex] ?? 0;
            const formattedValue = Number(value).toFixed(1);

            if (std > 0) {
              return `${formattedValue} ± ${std.toFixed(1)}`;
            }

            return formattedValue;
          }

          return String(value);
        },
      },
      title: {
        text: title,
        align: 'left',
        style: {
          color: labelColor,
          fontFamily: 'inherit',
        },
      },
      xaxis: xAxisConfig ?? {
        categories: categories,
        title: {
          text: xaxisTitle,
          style: {
            color: labelColor,
            fontFamily: 'inherit',
            ...(axisTitleStyle || {}),
          },
        },
        labels: {
          ...(horizontal && { formatter: formatAxisLabel }),
          ...(showAllCategoryLabels && {
            rotate: -45,
            trim: true,
            hideOverlappingLabels: false,
          }),
          style: {
            colors: mutedLabelColor,
            fontSize: '12px',
            fontFamily: 'inherit',
          },
        },
      },
      yaxis: yAxisConfig ?? {
        ...(yaxisMin !== undefined && { min: yaxisMin }),
        ...(yaxisMax !== undefined && { max: yaxisMax }),
        title: {
          text: yaxisTitle,
          style: {
            color: labelColor,
            fontFamily: 'inherit',
            ...(axisTitleStyle || {}),
          },
        },
        labels: {
          style: {
            colors: mutedLabelColor,
            fontSize: '12px',
            fontFamily: 'inherit',
          },
          ...(!horizontal && {
            formatter: yaxisLabelFormatter ?? formatAxisLabel,
          }),
          ...(horizontal && {
            tooltip: {
              enabled: true,
            },
          }),
        },
      },
      tooltip: {
        enabled: true,
        ...(tooltipCustom
          ? { custom: tooltipCustom }
          : { theme: isDark ? 'dark' : 'light' }),
        y: {
          formatter: tooltipFormatter ?? formatTooltipValue,
        },
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
        },
      },
      legend: legendConfig ?? {
        show: showLegend,
        position: legendPosition,
        labels: {
          colors: labelColor,
        },
        fontFamily: 'inherit',
        ...(legendOffsetY !== undefined && { offsetY: legendOffsetY }),
      },
      grid: gridConfig ?? {
        borderColor: defaultGridColor,
        strokeDashArray: 3,
      },
      fill: fillConfig ?? { opacity: 1 },
    }),
    [
      onBarDataPointClick,
      stacked,
      horizontal,
      distributed,
      columnWidth,
      borderRadius,
      colors,
      resolvedShowDataLabels,
      showStdDevLabels,
      dataLabelsFormatter,
      series,
      title,
      categories,
      xaxisTitle,
      yaxisTitle,
      isDark,
      tooltipCustom,
      tooltipFormatter,
      showLegend,
      legendPosition,
      showTotalDataLabels,
      showAllCategoryLabels,
      xAxisConfig,
      yAxisConfig,
      gridConfig,
      legendConfig,
      fillConfig,
      labelColor,
      mutedLabelColor,
      chartBackground,
      defaultGridColor,
      axisTitleStyle,
      legendOffsetY,
      yaxisMin,
      yaxisMax,
      yaxisLabelFormatter,
    ],
  );

  return (
    <ReactApexChart
      options={options}
      series={series}
      type="bar"
      height={height}
      width={width}
    />
  );
}

export default BarChart;
